import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import Loader from "../Loader/Loader";
import {
  useGetCampaignUsersQuery,
  useLazyExportCampaignUsersQuery,
} from "../../RTKQuery/CsvDataService/csvDataApi";
import { useGetSingleCampaignQuery } from "../../RTKQuery/CampaignService/campaignApi";
import DataTable from "../UIKit/CollapsibleTable";
import PageComponentHeader from "../PageComponentHeader";
import ExportButton from "../UIKit/Buttons/Export";
import UploadCampaignStats from "../UIKit/Buttons/UploadCampaignStats";
import FilterDrawer from "../UIKit/Drawer";
import ConditionalFilters from "../ConditionalFilters";
// import EmailTextModal from "../Modals/EmailTextModal";
import { CSVLink } from "react-csv";
import { generateCampaignCSV } from "../../utils/csv";
import { campaignDetailsColumns } from "../../constants/tableColumns";
// import { userListColumns } from "../../constants/tableColumns";
import { Box, Typography, Grid, Divider, Button, Paper } from "@mui/material";

const CampaignDetailsComponent = () => {
  const { id } = useParams();

  // State
  const [users, setUsers] = useState([]);
  const [email, setEmail] = useState("");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });
  const [filters, setFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [loader, setLoader] = useState(true);
  const [conditionCount, setConditionCount] = useState(0);
  const [subConditionCount, setSubConditionCount] = useState(0);

  const defaultFilter = [
    {
      id: "f-1",
      first: true,
      conditions: [
        {
          id: "f-10",
          first: true,
          subConditions: [],
          filter: "f-1",
        },
      ],
    },
  ];
  const [filterData, setFilterData] = useState(defaultFilter);
  const [closeFilterMenu, setCloseFilterMenu] = useState(true);
  const downloadRef = useRef();

  // RTK Query hooks
  const { data: campaignData, isLoading: fetchingCampaign } =
    useGetSingleCampaignQuery(id);
  const { data, isLoading } = useGetCampaignUsersQuery(
    {
      id,
      page: pagination.pageIndex,
      rows: pagination.pageSize,
      filters: JSON.stringify(filters),
      sorting: JSON.stringify(sorting),
    },
    { pollingInterval: 10000 }
  );

  const [triggerExport, { isLoading: exportLoading }] =
    useLazyExportCampaignUsersQuery();

  // Handlers
  const handleApplyFilters = () => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setFilters(filterData);
    setCloseFilterMenu(true);
  };

  const handleResetFilters = () => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setFilterData([
      {
        id: "f-1",
        first: true,
        conditions: [
          {
            id: "f-10",
            first: true,
            subConditions: [],
            filter: "f-1",
          },
        ],
      },
    ]);
    setFilters([]);
    setCloseFilterMenu(true);
  };

  const handleExport = async () => {
    try {
      await triggerExport({ id, filters }).unwrap();
    } catch (error) {
      console.error("Error exporting users:", error);
    }
  };

  // Effects
  useEffect(() => {
    if (data) {
      setUsers(data.users);
      setEmail(data?.users[0]?.campaigns[0]?.campaignBody || "");
      setLoader(false);
    }
  }, [data]);

  // Generate CSV data
  const csvData = generateCampaignCSV(users);

  return (
    <>
      <PageComponentHeader allowBack />

      <Box sx={{ padding: "2rem", margin: 0 }}>
        {/* Campaign Overview Section */}
        <Paper
          sx={{
            padding: "1.5rem",
            marginBottom: "2rem",
            borderRadius: "8px",
            border: "2px solid lightgray",
          }}
          elevation={1}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              marginBottom: "1rem",
              // color: "#8ABA5C",
            }}
          >
            {campaignData?.campaignTitle || "Campaign Title"}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ color: "gray", marginBottom: "1rem" }}
          >
            {campaignData?.campaignSubject || "No subject available"}
          </Typography>
          <Divider sx={{ marginBottom: "1rem" }} />
          <Grid container spacing={3}>
            <Grid item xs={6} md={4}>
              <Typography variant="body2" sx={{ color: "gray" }}>
                Total Sent
              </Typography>
              <Typography variant="h6">
                {campaignData?.totalSent || 0}
              </Typography>
            </Grid>
            <Grid item xs={6} md={4}>
              <Typography variant="body2" sx={{ color: "gray" }}>
                Total Opens
              </Typography>
              <Typography variant="h6">
                {campaignData?.totalOpen || 0}
              </Typography>
            </Grid>
            <Grid item xs={6} md={4}>
              <Typography variant="body2" sx={{ color: "gray" }}>
                Total Clicks
              </Typography>
              <Typography variant="h6">{campaignData?.clicks || 0}</Typography>
            </Grid>
            <Grid item xs={6} md={4}>
              <Typography variant="body2" sx={{ color: "gray" }}>
                Total Bounced
              </Typography>
              <Typography variant="h6">{campaignData?.bounced || 0}</Typography>
            </Grid>
            <Grid item xs={6} md={4}>
              <Typography variant="body2" sx={{ color: "gray" }}>
                Total Unsubscribed
              </Typography>
              <Typography variant="h6">
                {campaignData?.unsubScribed || 0}
              </Typography>
            </Grid>
            <Grid item xs={6} md={4}>
              <Typography variant="body2" sx={{ color: "gray" }}>
                Total Dropped
              </Typography>
              <Typography variant="h6">{campaignData?.dropped || 0}</Typography>
            </Grid>
            <Grid item xs={6} md={4}>
              <Typography variant="body2" sx={{ color: "gray" }}>
                Open Rate
              </Typography>
              <Typography variant="h6">
                {campaignData?.openRate?.toFixed(2) || "0.00"}%
              </Typography>
            </Grid>
            <Grid item xs={6} md={4}>
              <Typography variant="body2" sx={{ color: "gray" }}>
                Click Rate
              </Typography>
              <Typography variant="h6">
                {campaignData?.clickRate?.toFixed(2) || "0.00"}%
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Users Table Section */}
        {fetchingCampaign ? (
          <Loader />
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              Campaign Users
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                // marginTop: "1rem",
              }}
            >
              <ExportButton onClick={handleExport} isLoading={exportLoading} />
              {data?.users[0]?.campaigns[0]?.campaign === "lemlist" && (
                <UploadCampaignStats id={id} />
              )}
              <FilterDrawer
                close={closeFilterMenu}
                setClose={setCloseFilterMenu}
                setCloseFilterMenu={setCloseFilterMenu}
                filters={filters}
              >
                <ConditionalFilters
                  campaignUsers
                  onReset={handleResetFilters}
                  onSubmit={handleApplyFilters}
                  conditionCount={conditionCount}
                  setConditionCount={setConditionCount}
                  subConditionCount={subConditionCount}
                  setSubConditionCount={setSubConditionCount}
                  filtersArray={filterData}
                  setFiltersArray={setFilterData}
                  setCloseFilterMenu={setCloseFilterMenu}
                  defaultFilter={defaultFilter}
                />
              </FilterDrawer>
            </Box>
          </div>
        )}
        {loader ? (
          <div className="noData">
            <Loader />
          </div>
        ) : users?.length > 0 ? (
          <DataTable
            columns={campaignDetailsColumns}
            data={users}
            totalData={data?.total || 0}
            pagination={pagination}
            setPagination={setPagination}
            sorting={sorting}
            setSorting={setSorting}
            setLoader={setLoader}
          />
        ) : (
          <h3 className="noData">No Record Found</h3>
        )}
      </Box>
      <CSVLink
        headers={csvData?.headers || []}
        data={csvData?.data || ""}
        filename="Campaign"
        style={{ display: "none" }}
        ref={downloadRef}
      />
    </>
  );
};

export default CampaignDetailsComponent;
